import { createSlice } from "@reduxjs/toolkit";
import ToasterService from "utils/toaster.util";
import { NewFixedPricingState } from ".";
import { PayloadAction } from "@reduxjs/toolkit";
import { config } from "config";
import { SelectOption } from "components/atoms/Select";

const default_page_size = config.PAGE_SIZE;
const initialState: NewFixedPricingState = {
    pricings: [],
    pricing: null,
    loading: true,
    pricingsByZone: {},
    pricingsByZoneAndEngine: {},
    zoneOptions: [],
    priceClassesByZone: {},
    priceClassesOptions: [],
    refresh: 0,
    refreshLoader: false,
    current_filters: {},
    filters: { page: 1, page_size: default_page_size },
};

export const newFixedPricingSlice = createSlice({
    name: "newFixedPricing",
    initialState,
    reducers: {
        clearPricingAssign: (state) => {
            state.zoneOptions = [];
            state.priceClassesOptions = [];
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        addPricing: (state, action) => {
            let pricingsByZone: any = {};
            let pricings = state.pricings;
            pricings.unshift(action.payload);
            pricings.forEach((pricing: any) => {
                const { zone_id, company_id } = pricing;
                let zone_name = zone_id?._id || "Undefined";
                if (pricingsByZone[zone_name])
                    pricingsByZone[zone_name].pricings.push(pricing);
                else
                    pricingsByZone[zone_name] = {
                        pricings: [pricing],
                        zone_name: zone_id?.name,
                        company: company_id,
                    };
            });
            state.pricings = pricings;
            state.pricingsByZone = pricingsByZone;
        },
        updatePricing: (state, action) => {
            const { id, pricing } = action.payload;
            let pricingsByZone = state.pricingsByZone;
            state.pricings.every(({ _id }: { _id: string }, i: number) => {
                if (id === _id) {
                    state.pricings[i] = pricing;
                    return false;
                }
                return true;
            });
            for (const city in pricingsByZone) {
                if (Object.prototype.hasOwnProperty.call(pricingsByZone, city)) {
                    let done = false;
                    let element = pricingsByZone[city];
                    element.pricings.every(({ _id }: { _id: string }, i: number) => {
                        if (id === _id) {
                            done = true;
                            element.pricings[i] = pricing;
                            return false;
                        }
                        return true;
                    });
                    if (done) break;
                }
            }
        },
        setPricing: (state, action) => {
            state.pricing = action.payload;
        },
        setPricingByClass: (state, action) => {
            let class_id = action.payload;

            for (const pricing of state.pricings) {
                if (pricing.class_id._id === class_id) {
                    state.pricing = pricing;
                    break;
                }
            }

            if (!state.pricing)
                ToasterService.showError("Selected Class has no pricing yet!");
        },
        setPricings: (state, action) => {
            let pricingsByZone: any = {};
            let pricingsByZoneAndEngine: any = {};
            const pricings = action.payload;

            pricings.forEach((pricing: any) => {
                const { zone_id, company_id, price_engine_id } = pricing;
                
                if (zone_id) {
                    // First level: Group by zone and company
                    const zoneKey = `${zone_id._id}_${company_id._id}`;
                    
                    if (!pricingsByZoneAndEngine[zoneKey]) {
                        pricingsByZoneAndEngine[zoneKey] = {
                            zone_name: zone_id.name,
                            zone: zone_id,
                            company: company_id,
                            price_engines: {}
                        };
                    }

                    // Second level: Group by price engine within zone
                    if (price_engine_id) {
                        const engineKey = price_engine_id._id;
                        
                        if (!pricingsByZoneAndEngine[zoneKey].price_engines[engineKey]) {
                            pricingsByZoneAndEngine[zoneKey].price_engines[engineKey] = {
                                pricings: [],
                                engine_name: price_engine_id.name,
                                engine: price_engine_id
                            };
                        }

                        pricingsByZoneAndEngine[zoneKey].price_engines[engineKey].pricings.push(pricing);
                    }
                }

                // Keep the old grouping for backward compatibility
                let zone_name = zone_id?._id;
                if (pricingsByZone[zone_name]) {
                    pricingsByZone[zone_name].pricings.push(pricing);
                } else {
                    pricingsByZone[zone_name] = {
                        pricings: [pricing],
                        zone_name: zone_id?.name,
                        zone: zone_id,
                        company: company_id,
                    };
                }
            });
            
            state.pricings = pricings;
            state.refreshLoader = false;
            state.pricingsByZone = pricingsByZone;
            state.pricingsByZoneAndEngine = pricingsByZoneAndEngine;
        },
        setPriceClasses: (state, action) => {
            let classesByZone: any = {};
            const classes = action.payload;
            classes.forEach((classs: any) => {
                const { zone_id } = classs;
                let zone_name = zone_id?._id;

                if (classesByZone[zone_name])
                    classesByZone[zone_name].classes.push(classs);
                else
                    classesByZone[zone_name] = {
                        models_length: 0,
                        classes: [classs],
                        zone_name: zone_id?.name,
                    };
            });

            state.priceClassesByZone = classesByZone;
        },
        setPriceZones: (state, action) => {
            const zones = action.payload;
            let options: SelectOption[] = [];
            zones.forEach(({ _id, name }: any) => options.push({ value: _id, label: name }));
            state.zoneOptions = options;
        },
        setPriceClassesOptionsByZoneId: (state, action) => {
            const zone_id = action.payload;
            const classes = state.priceClassesByZone?.[zone_id]?.["classes"] || [];
            state.priceClassesOptions = classes.map(({ _id, name }: any) => ({
                value: _id,
                label: name,
            }));
        },
        setPricingsLocal: (state, action) => {
            const { payload } = action;
            state.pricingsByZoneAndEngine = payload;
        },
        refresh: (state) => {
            state.refresh += 1;
            state.refreshLoader = true;
        },
        resetPage: (state) => {
            state.filters.page = 1;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
            state.refreshLoader = true;
        },
        setCurrentFilters: (state, action) => {
            state.current_filters = action.payload;
        },
        resetFilters: (state) => {
            state.refresh += 1;
            state.refreshLoader = true;
            state.filters = initialState.filters;
            state.current_filters = initialState.current_filters;
        },
    }
});

const newFixedPricingReducer = newFixedPricingSlice.reducer;

export const newFixedPricingActions = newFixedPricingSlice.actions;
export default newFixedPricingReducer;
